import type { CRMIntegration } from '@alucio/aws-beacon-amplify/src/models';
import LocalStorageKeys from 'src/components/Authenticator/constants';
import { CRMConnectionStatus } from './crmMachineTypes';

export const CRM_AUTOMATIC_LOGIN_PREFERENCE_UPDATED = 'crmAutomaticLoginPreferenceUpdated';
export const CRM_AUTOMATIC_LOGIN_ATTEMPTS_UPDATED = 'crmAutomaticLoginAttemptsUpdated';
const CRM_AUTOMATIC_LOGIN_ATTEMPTS_KEY = 'crmAutomaticLoginAttempts';
const CRM_AUTOMATIC_LOGIN_MAX_ATTEMPTS = 3;

interface AutomaticLoginAvailabilityInput {
  crmIntegration?: CRMIntegration;
  isFeatureFlagEnabled: boolean;
}

interface InitialAutomaticLoginDecisionInput extends AutomaticLoginAvailabilityInput {
  hasReachedAttemptLimit: boolean;
  isCRMBusy: boolean;
  isOnline: boolean;
  isUserPreferenceEnabled: boolean;
  connectionStatus: CRMConnectionStatus;
}

export const getCRMAutomaticLoginPreference = (): boolean => {
  return localStorage.getItem(LocalStorageKeys.CRM_AUTOMATIC_LOGIN_PREFERENCE) !== 'false';
};

export const ensureCRMAutomaticLoginPreference = (): boolean => {
  if (localStorage.getItem(LocalStorageKeys.CRM_AUTOMATIC_LOGIN_PREFERENCE) === null) {
    setCRMAutomaticLoginPreference(true);
  }

  return getCRMAutomaticLoginPreference();
};

export const setCRMAutomaticLoginPreference = (isEnabled: boolean): void => {
  localStorage.setItem(LocalStorageKeys.CRM_AUTOMATIC_LOGIN_PREFERENCE, String(isEnabled));
  window.dispatchEvent(new CustomEvent(CRM_AUTOMATIC_LOGIN_PREFERENCE_UPDATED, {
    detail: { isEnabled },
  }));
};

export const clearCRMAutomaticLoginPreference = (): void => {
  localStorage.removeItem(LocalStorageKeys.CRM_AUTOMATIC_LOGIN_PREFERENCE);
  window.dispatchEvent(new CustomEvent(CRM_AUTOMATIC_LOGIN_PREFERENCE_UPDATED, {
    detail: { isEnabled: true },
  }));
};

export const getCRMAutomaticLoginAttemptCount = (): number => {
  const storedCount = Number(sessionStorage.getItem(CRM_AUTOMATIC_LOGIN_ATTEMPTS_KEY));
  return Number.isFinite(storedCount) ? storedCount : 0;
};

export const hasReachedCRMAutomaticLoginAttemptLimit = (): boolean => {
  return getCRMAutomaticLoginAttemptCount() >= CRM_AUTOMATIC_LOGIN_MAX_ATTEMPTS;
};

export const recordCRMAutomaticLoginAttempt = (): number => {
  const attemptCount = getCRMAutomaticLoginAttemptCount() + 1;
  sessionStorage.setItem(CRM_AUTOMATIC_LOGIN_ATTEMPTS_KEY, String(attemptCount));
  window.dispatchEvent(new CustomEvent(CRM_AUTOMATIC_LOGIN_ATTEMPTS_UPDATED, {
    detail: { attemptCount },
  }));
  return attemptCount;
};

export const clearCRMAutomaticLoginAttempts = (): void => {
  sessionStorage.removeItem(CRM_AUTOMATIC_LOGIN_ATTEMPTS_KEY);
  window.dispatchEvent(new CustomEvent(CRM_AUTOMATIC_LOGIN_ATTEMPTS_UPDATED, {
    detail: { attemptCount: 0 },
  }));
};

export const isCRMAutomaticLoginAvailable = ({
  crmIntegration,
  isFeatureFlagEnabled,
}: AutomaticLoginAvailabilityInput): boolean => {
  return isFeatureFlagEnabled && !!crmIntegration;
};

export const shouldAttemptInitialCRMAutomaticLogin = ({
  connectionStatus,
  hasReachedAttemptLimit,
  crmIntegration,
  isCRMBusy,
  isFeatureFlagEnabled,
  isOnline,
  isUserPreferenceEnabled,
}: InitialAutomaticLoginDecisionInput): boolean => {
  return (
    isCRMAutomaticLoginAvailable({ crmIntegration, isFeatureFlagEnabled }) &&
    isUserPreferenceEnabled &&
    isOnline &&
    !hasReachedAttemptLimit &&
    connectionStatus === CRMConnectionStatus.DISCONNECTED &&
    !isCRMBusy
  );
};
